/**
 * Login page — unified username/password form with SSO fallback.
 * Credentials are sent directly to Keycloak's token endpoint (ROPC grant).
 * SSO redirect (PKCE) remains available as a secondary option.
 *
 * THE ONLY BRANDED SURFACE A USER SEES BEFORE THEY HAVE AN IDENTITY (UI-004).
 * The brand is read from the `BrandingProvider`, whose read of
 * `GET /api/v1/branding` is public and unauthenticated, and whose context
 * publishes `DefaultBranding` both while that read is in flight and after it
 * failed - so this page NEVER blocks, spins or errors on the branding read
 * (RULE-001, D-BR-15/17). What the caller supplies is the APP SUFFIX and
 * nothing else: the heading `"<product name> - <suffix>"` is composed HERE, in
 * one place, so the separator, the ordering and the ASCII hyphen (RULE-064)
 * cannot drift between Admin, Management and Workspace, and the heading agrees
 * with the `document.title` the provider writes from the same two values
 * (D-BR-08).
 *
 * @module components/shared/LoginPage
 * @see TASK-MIG-AUTH-HARDEN
 * @see TASK-PB-LOGIN-BRAND
 * @see SPEC-PLATFORM-BRANDING-UI-004 / RULE-001 / RULE-002 / RULE-012
 * @see SPEC-PLATFORM-BRANDING-SCENARIO-005 / SCENARIO-015
 */
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Trans, useLingui } from "@lingui/react/macro";
import { useAuth } from "../../store/AuthContext";
import type { TenantRefusal } from "../../api/tenantAccess";
import { useBranding } from "../../branding/BrandingProvider";
import { DefaultBranding, type LocalizedText } from "../../branding/defaults";

export interface LoginPageProps {
  /**
   * THIS APPLICATION'S TITLE SUFFIX - `Admin`, `Management` or `Workspace` - as
   * a translated Lingui string the MOUNT owns (D-BR-08). The product name is
   * NOT translated and does NOT come from here: the page reads it from the
   * BrandingProvider and composes `"<product name> - <suffix>"` itself, so the
   * three applications cannot drift apart and no mount can reintroduce a vendor
   * literal (RULE-012).
   *
   * REQUIRED, deliberately: an optional suffix would need a default, and any
   * default here would be either a vendor literal (the defect this TASK removes)
   * or a silent "no suffix" that makes the three login pages indistinguishable.
   * A mount that forgets it fails to type-check.
   *
   * @see SPEC-PLATFORM-BRANDING-RULE-012
   * @see SPEC-PLATFORM-BRANDING-UI-004
   */
  appSuffix: string;
  /**
   * The legal-footer SLOT of SPEC-PLATFORM-BRANDING RULE-006a.
   *
   * The tenant and workspace applications pass an `<AppFooter />` here; the
   * platform admin application DELIBERATELY passes nothing, which is how one
   * shared login page serves a footer-bearing and a footer-free application
   * without forking it (D-BR-10, SCENARIO-015).
   *
   * When it is absent NOTHING is rendered - no wrapper, no spacer, no reserved
   * height - so the admin login carries no `contentinfo` landmark at all.
   *
   * It is a SLOT, not a coupling: this component does not import `AppFooter` and
   * never reads `branding.legal_footer`, and it keeps working for any consumer
   * that passes nothing. The filling mount decides what goes in it, including
   * how the provider's `loading` state maps onto the footer's states (D-BR-17).
   * (The page DOES read the BRAND half of the same context since
   * TASK-PB-LOGIN-BRAND - the product name, the logo and the welcome text - which
   * is a different half of the record and carries no legal claim.)
   *
   * @see SPEC-PLATFORM-BRANDING-RULE-006
   * @see SPEC-PLATFORM-BRANDING-UI-003
   */
  footer?: React.ReactNode;
}

/**
 * `hu` and `hu-HU` are Hungarian; every other UI locale resolves through `en`
 * (RULE-002's recorded divergence from SPEC-FRONTEND-RULE-006's seven locales).
 *
 * NOT IMPORTED FROM `AppFooter`, which holds the same two lines: the footer is a
 * SLOT this page deliberately does not import (D-BR-10), and pulling a helper out
 * of that module would create exactly the coupling TASK-PB-LOGIN-FOOTER-SLOT
 * avoided - the admin login would then load the footer module to render no
 * footer. The rule, not the implementation, is the shared thing.
 */
const isHungarianLocale = (locale: string): boolean =>
  (locale || "").toLowerCase().split(/[-_]/)[0] === "hu";

/**
 * RULE-002 for `login_welcome`: the UI locale's value when it is non-empty, else
 * the Hungarian value, else the empty string - which means DO NOT RENDER. An
 * empty welcome is never replaced by a placeholder, a greeting of our own or the
 * product name again: an operator who wrote no welcome gets no welcome line.
 */
const resolveWelcome = (
  value: LocalizedText | undefined,
  hungarian: boolean,
): string => {
  if (!value) return "";
  const preferred = (hungarian ? value.hu : value.en) ?? "";
  if (preferred.trim()) return preferred.trim();
  return (value.hu ?? "").trim();
};

export function LoginPage({
  appSuffix,
  footer,
}: LoginPageProps): React.ReactElement {
  const { login, loginWithCredentials, ssoEnabled, signInRefusal } = useAuth();
  // `i18n.locale` and not `useLocale()`: the login page renders inside the
  // Lingui context it already uses for every string on it, and reading the
  // locale from the SAME source keeps the welcome text and the labels around it
  // on one language. `useLocale()` would also add a second provider this shared
  // component throws without, on a surface whose whole point is that it renders.
  const { t, i18n } = useLingui();
  // The brand, fail-open by construction: the provider publishes DefaultBranding
  // while the bounded read is in flight AND after it failed, so there is no
  // loading branch, no error branch and no blocked form here (RULE-001, D-BR-15).
  const { branding } = useBranding();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The person's tenant is suspended or gone (R-MT-08): a short sentence that
  // says what to do (D-UIT-02), shown when a session ended for that reason or
  // a sign-in was refused for it.
  const tenantRefusalMessage = (refusal: TenantRefusal): string =>
    refusal === "auth/tenant-suspended"
      ? t`Access for your organization is suspended. Please contact your administrator.`
      : t`Your organization is no longer available. Please contact your administrator.`;
  const shownError = error ?? (signInRefusal ? tenantRefusalMessage(signInRefusal) : null);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  const handleCredentialLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;

    setCredentialLoading(true);
    setError(null);
    try {
      await loginWithCredentials(username.trim(), password);
    } catch (err) {
      // AuthContext throws stable, locale-agnostic error codes (loginErrorCode);
      // map them to localized messages here so they re-evaluate against the
      // active locale (useLingui().t is render-time bound). A server's (English)
      // text is never shown (EN sweep 2026-09-29, B-auth-invite).
      const code = err instanceof Error ? err.message : "";
      let message: string;
      switch (code) {
        case "auth/account-requires-password-change":
          message = t`Your account requires a password change. Please contact your administrator.`;
          break;
        case "auth/keycloak-not-configured":
          message = t`Authentication is not configured. Please contact your administrator.`;
          break;
        case "auth/tenant-suspended":
        case "auth/tenant-gone":
          message = tenantRefusalMessage(code);
          break;
        default:
          // Every other refusal - a wrong password, a disabled or locked
          // account, a rate limit, a gateway error - shows the page's own
          // login-failure line. No NEW descriptive sentence on an error surface
          // while D-318's reach beyond chat is unruled (PM, 2026-09-29); the code
          // and the server's text are in the console log.
          message = t`Login failed`;
      }
      setError(message);
      setCredentialLoading(false);
    }
  };

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    setError(null);
    try {
      await login();
    } catch {
      setError(t`SSO login failed`);
      setSsoLoading(false);
    }
  };

  const anyLoading = credentialLoading || ssoLoading;

  // RULE-016 guarantees a non-empty product name server-side and parseBranding
  // refuses an empty one; the tail is belt-and-braces, not a second policy.
  const productName = branding.product_name || DefaultBranding.product_name;
  // THE ONE COMPOSITION SITE, with an ASCII hyphen-minus (RULE-064, DASH-GATE).
  // The provider writes document.title from the same two values, so the tab and
  // the heading cannot disagree (D-BR-08).
  const heading = `${productName} - ${appSuffix}`;
  // `logo_url` is nullable by CONTRACT-001 - null is "no asset uploaded", not
  // "the read failed" - and the built-in field is typed nullable with it, which
  // is the only reason for the second `??`; `src={null}` is not something an
  // <img> can carry. The literal path lives in defaults.ts and is never retyped
  // here (RULE-012).
  const logoSrc = branding.logo_url ?? DefaultBranding.logo_url ?? undefined;
  const welcome = resolveWelcome(branding.login_welcome, isHungarianLocale(i18n.locale));

  return (
    // The outer layout box owns the full viewport height as a COLUMN, so the
    // credential card stays centred while the RULE-006a footer slot sits at the
    // bottom of the content column on a short and a tall viewport alike.
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{ p: 4, width: "100%", maxWidth: 420 }}
          data-testid="login-page"
        >
          {/* The installation logo, above the heading. Its accessible name is the
              PRODUCT NAME (RULE-046, the Sidebar/TenantHeader precedent): on a
              page whose heading is right below it that is redundant rather than
              wrong, and an empty alt would leave a branded installation's mark
              unnamed to a screen reader that reaches it before the heading. */}
          <Box
            component="img"
            src={logoSrc}
            alt={productName}
            data-testid="login-brand-logo"
            sx={{
              display: "block",
              mx: "auto",
              mb: 2,
              height: 56,
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />

          {/* The single <h1> of this page (RULE-046) - unchanged in level, tag
              and position; only its TEXT is now composed rather than passed in.
              `overflowWrap: anywhere` is the RULE-047 guarantee: a 120-character
              product name plus the Hungarian suffix WRAPS inside the 420 px
              card at 320 px rather than clipping or overflowing it. */}
          <Typography
            variant="h4"
            component="h1"
            textAlign="center"
            mb={welcome ? 1 : 3}
            data-testid="login-title"
            sx={{ overflowWrap: "anywhere" }}
          >
            {heading}
          </Typography>

          {/* ESCAPED, NEVER SANITISED (CONTRACT-005 as amended at the Wave 3
              audit close, D-BR-22). `login_welcome` is PLAIN TEXT bounded at 500
              runes per language by TASK-PB-SETTINGS-STORED-BRANDING, so it is
              rendered as a React child - React escapes it - and never through
              dangerouslySetInnerHTML, DOMPurify or utils/htmlSubset.ts, whose
              list is scoped to the two footer HTML fields. An empty value
              renders NOTHING: no placeholder, no spacer, no invented greeting
              (RULE-002). */}
          {welcome && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              mb={3}
              data-testid="login-welcome"
              sx={{ overflowWrap: "anywhere", whiteSpace: "pre-line" }}
            >
              {welcome}
            </Typography>
          )}

          {shownError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="login-error">
              {shownError}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleCredentialLogin}
            noValidate
          >
            <TextField
              fullWidth
              label={t`Username or email`}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={anyLoading}
              autoFocus
              autoComplete="username"
              sx={{ mb: 2 }}
              data-testid="login-username"
            />
            <TextField
              fullWidth
              label={t`Password`}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={anyLoading}
              autoComplete="current-password"
              sx={{ mb: 3 }}
              data-testid="login-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t`Toggle password visibility`}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={anyLoading || !username.trim() || !password}
              data-testid="credential-login-button"
            >
              {credentialLoading ? <Trans>Signing in...</Trans> : <Trans>Sign in</Trans>}
            </Button>
          </Box>

          {ssoEnabled && (
            <>
              <Divider sx={{ my: 3 }}>
                <Trans>or</Trans>
              </Divider>

              <Button
                variant="outlined"
                fullWidth
                onClick={handleSsoLogin}
                disabled={anyLoading}
                data-testid="sso-login-button"
              >
                {ssoLoading ? <Trans>Redirecting...</Trans> : <Trans>Sign in with SSO</Trans>}
              </Button>
            </>
          )}
        </Paper>
      </Container>

      {/*
        The footer slot (SPEC-PLATFORM-BRANDING RULE-006a). Rendered bare: an
        always-present wrapper would put an empty element - and, if it were a
        landmark, a `contentinfo` - on the platform admin login, which passes
        nothing here (SCENARIO-015).
      */}
      {footer}
    </Box>
  );
}

export default LoginPage;
